/* eslint-disable no-use-before-define */
import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import CockpitSyncUpNavigation from './CockpitSyncUpNavigation';
import { rechercherDiagnosticSyncUp } from './cockpitSyncUpApi';
import { FLUX_SYNCUP } from './catalogueFluxSyncUp';

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4), textAlign: 'left' },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2), boxShadow: 'none', border: '1px solid #e5e7eb' },
  entete: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: theme.spacing(1) },
  muted: { color: '#6b7280', fontSize: 12 },
  recherche: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap', marginTop: theme.spacing(1.5) },
  fluxGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: theme.spacing(1) },
  fluxBouton: { width: '100%', textAlign: 'left', justifyContent: 'space-between', minHeight: 76, padding: theme.spacing(1), textTransform: 'none' },
  fluxActif: { width: '100%', textAlign: 'left', justifyContent: 'space-between', minHeight: 76, padding: theme.spacing(1), textTransform: 'none', borderColor: '#111827', backgroundColor: '#f9fafb' },
  ligneFlux: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 },
  resumeBar: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', marginTop: theme.spacing(1) },
  chipVert: { backgroundColor: '#ecfdf5', color: '#065f46', fontSize: 11 },
  chipOrange: { backgroundColor: '#fff7ed', color: '#9a3412', fontSize: 11 },
  chipRouge: { backgroundColor: '#fef2f2', color: '#991b1b', fontSize: 11 },
  chipBleu: { backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: 11 },
  chipGris: { backgroundColor: '#f3f4f6', color: '#374151', fontSize: 11 },
  diagnosticHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1), flexWrap: 'wrap', marginBottom: theme.spacing(1.5) },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(1) },
  kpiBox: { border: '1px solid #e5e7eb', borderRadius: 6, padding: theme.spacing(1), backgroundColor: '#fff' },
  kpiValeur: { fontWeight: 700, fontSize: 18, lineHeight: 1.2 },
  kpiLabel: { color: '#6b7280', fontSize: 11, textTransform: 'uppercase' },
  preuvesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: theme.spacing(1.5), marginTop: theme.spacing(1.5) },
  preuveSection: { border: '1px solid #e5e7eb', borderRadius: 6, padding: theme.spacing(1), backgroundColor: '#fff' },
  preuveItem: { borderTop: '1px solid #f3f4f6', paddingTop: theme.spacing(0.75), marginTop: theme.spacing(0.75) },
  projectionGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: theme.spacing(1.5), marginTop: theme.spacing(1.5) },
  projectionBox: { border: '1px solid #e5e7eb', borderRadius: 6, padding: theme.spacing(1), backgroundColor: '#fbfdff' },
  code: { fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  erreurBox: { borderLeft: '4px solid #f59e0b', padding: theme.spacing(1.5), backgroundColor: '#fff7ed', marginBottom: theme.spacing(2) },
}));

const CockpitSyncUp = ({ vue = 'diagnostics' }) => {
  const classes = useStyles();
  const [requete, setRequete] = useState('');
  const [resultat, setResultat] = useState(null);
  const [fluxActif, setFluxActif] = useState('parent-enfants');
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');

  const rechercher = (requeteRecherche = requete) => {
    const q = String(requeteRecherche || '').trim();
    if (!q) return;
    setChargement(true);
    rechercherDiagnosticSyncUp({ requete: q })
      .then((payload) => {
        setResultat(payload);
        setErreur('');
        if (!payload.diagnostics.find((diagnostic) => diagnostic.flux.id === fluxActif)) {
          setFluxActif('parent-enfants');
        }
      })
      .catch((err) => setErreur(err.message))
      .finally(() => setChargement(false));
  };

  const diagnostics = (resultat && resultat.diagnostics) || [];
  const diagnosticActif = useMemo(() => (
    diagnostics.find((diagnostic) => diagnostic.flux.id === fluxActif)
    || diagnostics.find((diagnostic) => diagnostic.flux.id === 'parent-enfants')
    || diagnostics[0]
  ), [diagnostics, fluxActif]);

  if (vue === 'catalogue') {
    return (
      <Container maxWidth={false} className={classes.page}>
        <CockpitSyncUpNavigation vueActive="catalogue" />
        <CatalogueFlux classes={classes} />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} className={classes.page}>
      <CockpitSyncUpNavigation vueActive="diagnostics" />

      <Paper className={classes.paper}>
        <div className={classes.entete}>
          <div>
            <Typography variant="h4">Diagnostic fonctionnel SyncUp</Typography>
            <Typography variant="body2" className={classes.muted}>
              Recherche en lecture seule sur les donnees reelles SyncUp. Le premier flux interprete est Parent -&gt; Enfants.
            </Typography>
          </div>
          <Chip size="small" label="Lecture seule" className={classes.chipBleu} />
        </div>

        <form
          className={classes.recherche}
          onSubmit={(event) => {
            event.preventDefault();
            rechercher();
          }}
        >
          <TextField
            label="Utilisateur"
            value={requete}
            onChange={(event) => setRequete(event.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 320 }}
            placeholder="ID, courriel ou nom utilisateur"
          />
          <Button type="submit" variant="contained" color="primary" disabled={chargement}>
            {chargement ? 'Recherche...' : 'Diagnostiquer'}
          </Button>
        </form>
      </Paper>

      {erreur && (
        <div className={classes.erreurBox}>
          <Typography variant="body2">Erreur : {erreur}</Typography>
        </div>
      )}

      {resultat && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ResumeObjet resultat={resultat} classes={classes} />
          </Grid>
          {(resultat.avertissements || []).length > 0 && (
            <Grid item xs={12}>
              <Avertissements resultat={resultat} classes={classes} />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <ListeFlux diagnostics={diagnostics} fluxActif={fluxActif} setFluxActif={setFluxActif} classes={classes} />
          </Grid>
          <Grid item xs={12} md={8}>
            {diagnosticActif && <DiagnosticDetail diagnostic={diagnosticActif} classes={classes} />}
          </Grid>
        </Grid>
      )}

      {!resultat && !erreur && (
        <Paper className={classes.paper}>
          <Typography variant="h6">Aucune investigation lancee</Typography>
          <Typography variant="body2" className={classes.muted}>
            Saisissez un utilisateur SyncUp reel pour calculer les diagnostics depuis PostgreSQL.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

function ResumeObjet({ resultat, classes }) {
  const objet = resultat.objetRacine || {};
  const interpretes = resultat.diagnostics.filter((diagnostic) => diagnostic.etat !== 'non_interprete').length;
  const bloques = resultat.diagnostics.filter((diagnostic) => diagnostic.etat === 'bloque').length;

  return (
    <Paper className={classes.paper}>
      <div className={classes.entete}>
        <div>
          <Typography variant="h6">{objet.libelle}</Typography>
          <Typography variant="body2" className={classes.muted}>
            {objet.type} {objet.id ? `- ${objet.id}` : ''} {objet.organisationId ? `- ${objet.organisationId}` : ''}
          </Typography>
        </div>
        <div className={classes.resumeBar}>
          <Chip size="small" label={`Etat: ${objet.etat || 'inconnu'}`} className={classeEtat(objet.etat === 'actif' ? 'sain' : 'inconnu', classes)} />
          <Chip size="small" label={`${interpretes} flux interprete(s)`} className={classes.chipBleu} />
          <Chip size="small" label={`${bloques} bloque(s)`} className={bloques > 0 ? classes.chipRouge : classes.chipGris} />
        </div>
      </div>
    </Paper>
  );
}

function Avertissements({ resultat, classes }) {
  return (
    <Paper className={classes.paper}>
      <Typography variant="h6">Sources d observabilite incompletes</Typography>
      <Typography variant="body2" className={classes.muted}>
        Le diagnostic reste calcule depuis les sources reelles disponibles; ces avertissements indiquent les signaux manquants.
      </Typography>
      <div className={classes.resumeBar}>
        {(resultat.avertissements || []).map((avertissement) => (
          <Chip key={avertissement} size="small" label={avertissement} className={classes.chipOrange} />
        ))}
      </div>
      {resultat.sourcesSql && (
        <details style={{ marginTop: 8 }}>
          <summary className={classes.muted}>Sources SQL detectees</summary>
          <div className={classes.code}>{JSON.stringify(resultat.sourcesSql, null, 2)}</div>
        </details>
      )}
    </Paper>
  );
}

function ListeFlux({ diagnostics, fluxActif, setFluxActif, classes }) {
  return (
    <Paper className={classes.paper}>
      <Typography variant="h6">Flux diagnostiques</Typography>
      <Typography variant="body2" className={classes.muted}>
        Les flux a venir gardent le meme contrat de diagnostic.
      </Typography>
      <div className={classes.fluxGrid} style={{ marginTop: 12 }}>
        {diagnostics.map((diagnostic) => {
          const actif = diagnostic.flux.id === fluxActif;
          return (
            <Button
              key={diagnostic.flux.id}
              variant="outlined"
              className={actif ? classes.fluxActif : classes.fluxBouton}
              onClick={() => setFluxActif(diagnostic.flux.id)}
            >
              <span className={classes.ligneFlux}>
                <strong>{diagnostic.flux.nom}</strong>
                <span className={classes.muted}>{diagnostic.raisonPrincipale}</span>
              </span>
              <Chip size="small" label={libelleEtat(diagnostic.etat)} className={classeEtat(diagnostic.etat, classes)} />
            </Button>
          );
        })}
      </div>
    </Paper>
  );
}

function DiagnosticDetail({ diagnostic, classes }) {
  return (
    <Paper className={classes.paper}>
      <div className={classes.diagnosticHeader}>
        <div>
          <Typography variant="h5">{diagnostic.flux.nom}</Typography>
          <Typography variant="body2" className={classes.muted}>{diagnostic.flux.objectif}</Typography>
        </div>
        <div className={classes.resumeBar}>
          <Chip size="small" label={libelleEtat(diagnostic.etat)} className={classeEtat(diagnostic.etat, classes)} />
          <Chip size="small" label={`Confiance: ${diagnostic.scoreConfiance}`} className={classeConfiance(diagnostic.scoreConfiance, classes)} />
        </div>
      </div>

      <Typography variant="subtitle1">{diagnostic.raisonPrincipale}</Typography>
      <Typography variant="body2" style={{ marginTop: 6 }}>{diagnostic.interpretation}</Typography>

      <div className={classes.kpiGrid}>
        <Kpi classes={classes} label="Preuves positives" valeur={diagnostic.preuvesPositives.length} />
        <Kpi classes={classes} label="Preuves negatives" valeur={diagnostic.preuvesNegatives.length} />
        <Kpi classes={classes} label="Preuves manquantes" valeur={diagnostic.preuvesManquantes.length} />
        <Kpi classes={classes} label="Contradictions" valeur={diagnostic.contradictions.length} />
      </div>

      <div className={classes.projectionGrid}>
        <Projection titre="Projection attendue" projection={diagnostic.projectionAttendue} classes={classes} />
        <Projection titre="Projection observee" projection={diagnostic.projectionObservee} classes={classes} />
      </div>

      <div className={classes.preuvesGrid}>
        <Preuves titre="Preuves positives" preuves={diagnostic.preuvesPositives} classes={classes} />
        <Preuves titre="Preuves negatives" preuves={diagnostic.preuvesNegatives} classes={classes} />
        <Preuves titre="Preuves manquantes" preuves={diagnostic.preuvesManquantes} classes={classes} />
        <Preuves titre="Contradictions" preuves={diagnostic.contradictions} classes={classes} />
      </div>

      <Paper className={classes.projectionBox} style={{ marginTop: 12 }}>
        <Typography variant="subtitle2">Prochaine verification recommandee</Typography>
        <Typography variant="body2">{diagnostic.prochaineVerification}</Typography>
      </Paper>
    </Paper>
  );
}

function Preuves({ titre, preuves, classes }) {
  return (
    <div className={classes.preuveSection}>
      <Typography variant="subtitle2">{titre}</Typography>
      {preuves.length === 0 && <Typography variant="body2" className={classes.muted}>Aucune.</Typography>}
      {preuves.map((preuve, index) => (
        <div key={`${preuve.code}-${index}`} className={classes.preuveItem}>
          <Typography variant="body2"><strong>{preuve.libelle}</strong></Typography>
          <Typography variant="body2" className={classes.muted}>
            {preuve.source} {preuve.objetId ? `- ${preuve.objetId}` : ''} {preuve.horodatage ? `- ${formaterDate(preuve.horodatage)}` : ''}
          </Typography>
        </div>
      ))}
    </div>
  );
}

function Projection({ titre, projection, classes }) {
  const enfants = (projection && projection.enfants) || [];
  return (
    <div className={classes.projectionBox}>
      <Typography variant="subtitle2">{titre}</Typography>
      {enfants.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Enfant</TableCell>
              <TableCell>ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enfants.map((enfant) => (
              <TableRow key={enfant.id}>
                <TableCell>{enfant.nom || enfant.id}</TableCell>
                <TableCell className={classes.muted}>{enfant.id}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" className={classes.muted}>Aucun enfant visible.</Typography>
      )}
      <details style={{ marginTop: 8 }}>
        <summary className={classes.muted}>JSON</summary>
        <div className={classes.code}>{JSON.stringify(projection || {}, null, 2)}</div>
      </details>
    </div>
  );
}

function CatalogueFlux({ classes }) {
  return (
    <Paper className={classes.paper}>
      <Typography variant="h4">Catalogue des flux SyncUp</Typography>
      <Typography variant="body2" className={classes.muted}>
        Structure extensible issue de l ADR. Parent -&gt; Enfants est le premier flux interprete.
      </Typography>
      <div style={{ marginTop: 12 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Flux</TableCell>
              <TableCell>Categorie</TableCell>
              <TableCell>Etat</TableCell>
              <TableCell>Objectif</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {FLUX_SYNCUP.map((flux) => (
              <TableRow key={flux.id}>
                <TableCell><strong>{flux.nom}</strong></TableCell>
                <TableCell>{flux.categorie}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={flux.id === 'parent-enfants' ? 'Interprete' : 'A venir'}
                    className={flux.id === 'parent-enfants' ? classes.chipVert : classes.chipGris}
                  />
                </TableCell>
                <TableCell>{flux.objectif}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
}

function Kpi({ classes, valeur, label }) {
  return (
    <div className={classes.kpiBox}>
      <div className={classes.kpiValeur}>{valeur}</div>
      <div className={classes.kpiLabel}>{label}</div>
    </div>
  );
}

function libelleEtat(etat) {
  const libelles = {
    sain: 'Sain',
    degrade: 'Degrade',
    bloque: 'Bloque',
    contradictoire: 'Contradictoire',
    inconnu: 'Inconnu',
    non_interprete: 'Non interprete',
  };
  return libelles[etat] || etat;
}

function classeEtat(etat, classes) {
  if (etat === 'sain') return classes.chipVert;
  if (etat === 'bloque' || etat === 'contradictoire') return classes.chipRouge;
  if (etat === 'degrade' || etat === 'inconnu') return classes.chipOrange;
  return classes.chipGris;
}

function classeConfiance(score, classes) {
  if (score === 'fort') return classes.chipVert;
  if (score === 'moyen') return classes.chipBleu;
  if (score === 'faible') return classes.chipOrange;
  return classes.chipGris;
}

function formaterDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('fr-CA', { timeZone: 'America/Toronto', hour12: false });
}

export default CockpitSyncUp;



// WEBPACK FOOTER //
// src/components/cockpitSyncUp/CockpitSyncUp.js